import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  DatePicker,
  Form,
  InputNumber,
  Modal,
  notification,
} from 'antd';
import dayjs from 'dayjs';

import ReferencesSelector, {
  EMPTY_ID,
} from '@totem/components/common/reference/ReferenceSelector';
import {
  ObjectReference,
  OrganizationConfiguration,
} from '@totem/components/services/remoteAccess/auditConfiguration/types';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';

type Props = {
  open: boolean;
  organizationConfig: OrganizationConfiguration;
  existingBuildingIds: string[];
  defaultNextAudit: string;
  defaultCadenceMonths: number;
  isSaving: boolean;
  onCancel: () => void;
  onSubmit: (payload: {
    building: ObjectReference;
    nextAudit: string;
    cadenceMonths: number;
  }) => void;
};

const FormItem = Form.Item;

const buildDefaultReference = (organizationId: string) => ({
  organizationId,
  regionId: EMPTY_ID,
  buildingId: EMPTY_ID,
  controlSystemId: EMPTY_ID,
});

const AddBuildingModal = ({
  open,
  organizationConfig,
  existingBuildingIds,
  defaultNextAudit,
  defaultCadenceMonths,
  isSaving,
  onCancel,
  onSubmit,
}: Props) => {
  const [selectedBuilding, setSelectedBuilding] =
    useState<ObjectReference>(null);
  const [nextAudit, setNextAudit] = useState<string>(defaultNextAudit);
  const [cadenceMonths, setCadenceMonths] =
    useState<number>(defaultCadenceMonths);

  useEffect(() => {
    if (!open) {
      return;
    }
    setSelectedBuilding(null);

    setNextAudit(defaultNextAudit);

    setCadenceMonths(defaultCadenceMonths);
  }, [open, defaultNextAudit, defaultCadenceMonths]);

  const referenceDefaults = useMemo(() => {
    if (!organizationConfig?.organization?.id) {
      return buildDefaultReference(EMPTY_ID);
    }
    return buildDefaultReference(organizationConfig.organization.id);
  }, [organizationConfig]);

  const handleSubmit = () => {
    if (!selectedBuilding || selectedBuilding.id === EMPTY_ID) {
      notification.error({
        title: 'Select a building',
        description: 'Please choose a building before saving.',
        duration: 3,
      });
      return;
    }
    if (existingBuildingIds.includes(selectedBuilding.id)) {
      notification.error({
        title: 'Building already configured',
        description: 'This building is already in the audit configuration.',
        duration: 3,
      });
      return;
    }
    if (!nextAudit || !cadenceMonths) {
      notification.error({
        title: 'Missing values',
        description: 'Next audit date and cadence are required.',
        duration: 3,
      });
      return;
    }
    onSubmit({
      building: selectedBuilding,
      nextAudit,
      cadenceMonths,
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={<ModalTitle>Add Building</ModalTitle>}
      confirmLoading={false}
      width={'40%'}
      footer={
        <ModalFooter>
          <Button onClick={onCancel}>Close</Button>
          <Button onClick={handleSubmit} type="primary" disabled={isSaving}>
            Add
          </Button>
        </ModalFooter>
      }
    >
      <Form layout="vertical">
        <FormItem label="Region / Building" colon={false} required>
          <ReferencesSelector
            references={referenceDefaults}
            showOrganization={false}
            showRegion
            showBuilding
            showControlSystem={false}
            onBuildingChanged={(building) => setSelectedBuilding(building)}
          />
        </FormItem>
        <FormItem label="Next Audit" colon={false} required>
          <DatePicker
            value={nextAudit ? dayjs(nextAudit) : null}
            onChange={(value) => {
              if (value) {
                setNextAudit(value.toISOString());
              }
            }}
            style={{ width: '100%' }}
          />
        </FormItem>
        <FormItem label="Cadence (months)" colon={false} required>
          <InputNumber
            min={1}
            value={cadenceMonths}
            onChange={(value) => {
              if (typeof value === 'number') {
                setCadenceMonths(value);
              }
            }}
            style={{ width: '100%' }}
          />
        </FormItem>
      </Form>
    </Modal>
  );
};

export default AddBuildingModal;
